import { useMemo, useState } from "react";
import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import { characters, factionTabs } from "@/mocks/characters";

export default function Characters() {
  const [active, setActive] = useState("all");

  const list = useMemo(() => {
    if (active === "all") {
      return characters;
    }
    return characters.filter((item) => item.faction === active);
  }, [active]);

  return (
    <section id="characters" className="w-full bg-background-100 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="角色图鉴"
            title="主要角色一览"
            desc="按阵营筛选，快速找到你关心的角色。每位角色都标注了身份、火焰属性与人物要点，帮助你理清群像之间的关系。"
          />
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="角色阵营筛选">
            {factionTabs.map((tab) => {
              const isActive = active === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(tab.id)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((char, index) => (
            <Reveal key={char.id} delay={(index % 3) * 90}>
              <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
                <div className="relative h-56 w-full overflow-hidden bg-background-200">
                  <img
                    src={char.image}
                    alt={`${char.name}的角色插画`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-foreground-950/70 px-3 py-1 text-xs text-background-50 backdrop-blur-sm">
                    {char.role}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-foreground-950">{char.name}</h3>
                      <p className="mt-0.5 text-xs text-foreground-500">{char.en}</p>
                    </div>
                    {char.flame ? (
                      <span className="flex shrink-0 items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                        <i className="ri-fire-fill text-[11px]" />
                        {char.flame}之焰
                      </span>
                    ) : (
                      <span className="flex shrink-0 items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
                        非能力者
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-sm font-medium text-primary-700">{char.title}</p>
                  <p className="mt-2 flex-1 text-sm leading-6 text-foreground-700">{char.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}